import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users, IndianRupee, CalendarCheck, Briefcase, CalendarDays,
  TrendingUp, TrendingDown, ArrowRight, UserPlus, ClipboardCheck, Plus, Receipt, Cake, MessageSquare, Mail, Sparkles
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
// Phase 220 — In-app onboarding walkthrough player (self-service)
import SelfServiceWalkthroughSection from '@/components/dashboard/SelfServiceWalkthroughSection';
import DashboardGetAppButton from '@/components/DashboardGetAppButton';
// Phase 282c — Weekly Activation Recap tile (Monday email preview)
import WeeklyActivationTile from '@/components/dashboard/WeeklyActivationTile';
// Phase 282d — Quarterly Star Academy badge (auto-hides when no win)
import StarAcademyTile from '@/components/dashboard/StarAcademyTile';
// Phase 293 — Payment Verified tile (badge + nudge to run Health Check Wizard)
import PaymentVerifiedTile from '@/components/dashboard/PaymentVerifiedTile';
// Phase 262 — Academy Challenge gamification leaderboard
import AcademyChallengeTile from '@/components/dashboard/AcademyChallengeTile';
// Phase 266d — Top Moments embed performance (self-hides if zero impressions)
import EmbedPerformanceTile from '@/components/EmbedPerformanceTile';
// Phase 290.c — Sticky weekly-goal reminder strip (academy_owner only)
import WeeklyGoalBanner from '@/components/dashboard/WeeklyGoalBanner';
// Phase 310.5 — AI Recovery ROI hero tile (auto-hides on ₹0 / fetch error)
import RoiHeroTile from '@/components/dashboard/RoiHeroTile';
// Phase 323b — Self-Signup QR card (academy_owner only)
import DashboardSelfSignupQRCard from '@/components/dashboard/DashboardSelfSignupQRCard';
// Phase 323c — Weekly "Re-share your signup QR" dashboard nudge
import SignupNudgeBanner from '@/components/dashboard/SignupNudgeBanner';
// Phase 326c — Self-Signup Funnel Insights (academy_owner only; self-hides on all-zero)
import FunnelInsightsTile from '@/components/dashboard/FunnelInsightsTile';
// Phase 639 — First-Run Wizard (auto-shows for brand-new owners)
import FirstRunWizard from '@/components/onboarding/FirstRunWizard';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [activity, setActivity] = useState([]);
  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 102 — Upcoming Birthdays widget
  const [birthdays, setBirthdays] = useState([]);
  const [sendingWish, setSendingWish] = useState(null);
  // Phase 106B — Zero-state onboarding card for brand-new tenants
  const [onboarding, setOnboarding] = useState(null);
  // Phase 261i — Feature flag for the "Get App" quick-action CTA so super
  // admins can run install-surface A/B tests. Default true = current behaviour.
  const showGetAppCta = useFeatureFlag('show_pwa_dashboard_cta', true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, chartRes, activityRes, acadRes, bdayRes, onbRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/revenue-chart'),
          api.get('/dashboard/recent-activity'),
          api.get('/academy').catch(() => null),
          api.get('/dashboard/upcoming-birthdays', { params: { days: 14 } }).catch(() => ({ data: [] })),
          api.get('/dashboard/onboarding-status').catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setChartData(chartRes.data);
        setActivity(activityRes.data);
        if (acadRes) setAcademy(acadRes.data);
        setBirthdays(bdayRes.data || []);
        setOnboarding(onbRes.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSendWish = async (student, channel) => {
    setSendingWish(`${student.id}-${channel}`);
    try {
      const res = await api.post(`/students/${student.id}/birthday-wish`, { channel });
      toast.success(`Birthday wish sent via ${channel} to ${res.data.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to send via ${channel}`);
    } finally {
      setSendingWish(null);
    }
  };

  // Phase 639 — friendlier empty-state design. Instead of scary
  // "0%" or "-₹230K" numbers when the academy has just signed up,
  // swap the KPI card body for a first-run CTA nudging the owner to
  // the exact next action. Only kicks in when the underlying data is
  // genuinely absent (never for real zero after activity).
  const stu = stats?.total_students || 0;
  const attToday = stats?.attendance_today || 0;
  const feesPending = stats?.fees_pending || 0;
  const feesCollected = stats?.fees_collected || 0;
  const netProfit = stats?.net_profit || 0;
  const hasFinanceData = feesCollected > 0 || feesPending > 0 || (stats?.total_employees || 0) > 0;

  const kpis = stats ? [
    {
      label: 'Total Students', value: stu, icon: Users,
      change: (stats.active_students || 0) + ' active', color: 'text-teal-600',
      empty: stu === 0,
      emptyCta: { text: 'Add your first student →', href: '/students', emoji: '➕' },
    },
    {
      label: 'Attendance Today',
      value: (stats.attendance_rate || 0) + '%', icon: CalendarCheck,
      change: attToday + ' present', color: 'text-blue-600',
      empty: stu > 0 && attToday === 0,
      emptyCta: { text: 'Mark today\'s attendance →', href: '/students', emoji: '✓' },
    },
    {
      label: 'Fees Pending',
      value: '₹' + (feesPending / 1000).toFixed(1) + 'K', icon: IndianRupee,
      change: '₹' + (feesCollected / 1000).toFixed(1) + 'K collected', color: 'text-amber-600',
      empty: feesPending === 0 && feesCollected === 0,
      emptyCta: { text: 'Set up your first fee →', href: '/fees', emoji: '💸' },
    },
    {
      label: 'Net Profit',
      value: '₹' + (netProfit / 1000).toFixed(1) + 'K', icon: TrendingUp,
      change: (stats.total_employees || 0) + ' employees', color: 'text-emerald-600',
      // Only show the -₹ number once there's actual finance activity;
      // otherwise nudge to the finance module instead of scaring the user.
      empty: !hasFinanceData,
      emptyCta: { text: 'Track your first expense →', href: '/finance', emoji: '📊' },
    },
  ] : [];

  const quickActions = [
    { label: 'Add Student', icon: UserPlus, href: '/students', color: 'bg-teal-600 hover:bg-teal-700' },
    { label: 'Mark Attendance', icon: ClipboardCheck, href: '/students', color: 'bg-blue-600 hover:bg-blue-700' },
    { label: 'Create Event', icon: Plus, href: '/events', color: 'bg-amber-600 hover:bg-amber-700' },
    { label: 'Add Expense', icon: Receipt, href: '/finance', color: 'bg-slate-600 hover:bg-slate-700' },
  ];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div><Skeleton className="h-8 w-64" /></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Phase 639 — First-Run Wizard. Auto-shows for owners with
          fewer than 3 students AND no first_run_completed_at marker.
          Self-manages via localStorage after dismissal. */}
      {user?.role === 'academy_owner' && academy && (
        <FirstRunWizard
          academy={academy}
          students={{ length: onboarding?.student_count || 0 }}
        />
      )}
      {/* Phase 310.5 — AI Recovery ROI hero tile. Self-hides when there's
          nothing recovered yet, so brand-new academies aren't greeted by
          a "₹0 recovered" tile that under-sells the feature. */}
      {(user?.role === 'academy_owner' || user?.role === 'super_admin') && (
        <RoiHeroTile />
      )}
      {/* Phase 290.c — Weekly Goal sticky banner. Self-hides on fetch
          failure, dismissed-per-week via localStorage. Only renders for
          academy_owner / super_admin since the API is role-gated. */}
      {(user?.role === 'academy_owner' || user?.role === 'super_admin') && (
        <WeeklyGoalBanner />
      )}
      {/* Phase 106B — Zero-state welcome banner for brand-new tenants */}
      {onboarding?.is_empty_state && (
        <div className="rounded-xl border-2 border-teal-300 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50 dark:from-teal-950/20 dark:via-emerald-950/20 dark:to-cyan-950/20 p-6 relative overflow-hidden" data-testid="dashboard-welcome-banner">
          <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-teal-200/40 blur-2xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shrink-0 shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-display font-semibold text-teal-900 mb-1">{onboarding.welcome_title || "Welcome to Out-Play!"}</h2>
              <p className="text-sm text-teal-800/90 leading-relaxed">{onboarding.welcome_message}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <a href="/settings" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium shadow-sm transition-colors" data-testid="welcome-cta-settings">
                  <Plus className="h-3.5 w-3.5" /> {onboarding.suggested_next_step === 'add_courses' ? 'Set up Courses' : onboarding.suggested_next_step === 'add_batches' ? 'Add Batches' : 'Go to Settings'}
                </a>
                {onboarding.has_courses && (
                  <a href="/students" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white hover:bg-teal-50 text-teal-700 border border-teal-300 text-xs font-medium transition-colors" data-testid="welcome-cta-students">
                    <UserPlus className="h-3.5 w-3.5" /> Add your first student
                  </a>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded border ${onboarding.has_courses ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                  {onboarding.has_courses ? '✓' : '○'} Courses
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded border ${onboarding.has_batches ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                  {onboarding.has_batches ? '✓' : '○'} Batches
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded border ${onboarding.has_students ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                  {onboarding.has_students ? '✓' : '○'} Students ({onboarding.student_count || 0})
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded border ${onboarding.has_revenue ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500'}`}>
                  {onboarding.has_revenue ? '✓' : '○'} First Payment
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 323b — Self-Signup QR card (academy_owner only) */}
      {user?.role === 'academy_owner' && (
        <DashboardSelfSignupQRCard role="student" />
      )}

      {/* Phase 326c — Self-Signup Funnel Insights (academy_owner only) */}
      {user?.role === 'academy_owner' && (
        <FunnelInsightsTile days={30} />
      )}

      {/* Phase 323c — Weekly nudge (auto-hides on signups/dismissal) */}
      {user?.role === 'academy_owner' && (
        <SignupNudgeBanner />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-semibold">
          Welcome back, {academy?.name || user?.name?.split(' ')[0]}
        </h1>
        <div className="flex items-center gap-2 mt-1">
          {academy?.custom_id && <Badge variant="outline" className="text-xs font-mono">{academy.custom_id}</Badge>}
          <p className="text-sm text-muted-foreground">Here&apos;s what&apos;s happening at your academy today.</p>
        </div>
      </div>

      {/* KPI Cards — Phase 639 empty-state redesign */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          const testId = `kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`;
          if (kpi.empty && kpi.emptyCta) {
            return (
              <Link key={kpi.label} to={kpi.emptyCta.href}
                    className="block group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
                    data-testid={`${testId}-empty`}>
                <Card className="border-dashed hover:border-primary hover:bg-primary/5 transition-colors h-full">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">{kpi.label}</span>
                      <div className={`h-8 w-8 rounded-lg bg-muted flex items-center justify-center ${kpi.color} opacity-70`}>
                        <Icon className="h-4 w-4" />
                      </div>
                    </div>
                    <p className="text-lg leading-tight font-medium text-slate-700 dark:text-slate-200 group-hover:text-primary transition-colors">
                      <span className="mr-1">{kpi.emptyCta.emoji}</span>{kpi.emptyCta.text}
                    </p>
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">Nothing here yet — tap to start</p>
                  </CardContent>
                </Card>
              </Link>
            );
          }
          return (
            <Card key={kpi.label} className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid={testId}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">{kpi.label}</span>
                  <div className={`h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center ${kpi.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
                <p className="text-2xl font-display font-semibold tabular-nums">{kpi.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{kpi.change}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <Link key={action.label} to={action.href}>
              <Button size="sm" className={`${action.color} text-white`} data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon className="h-4 w-4 mr-1.5" /> {action.label}
              </Button>
            </Link>
          );
        })}
        {/* Phase 261g — Persistent "Get App" CTA. Hides when already installed.
            Phase 261i — Also hides when feature flag `show_pwa_dashboard_cta`
            resolves to false (default true). */}
        {showGetAppCta && <DashboardGetAppButton />}
      </div>

      {/* Phase 282c — Monday Activation Recap (academy_owner only) */}
      {user?.role === 'academy_owner' && (
        <WeeklyActivationTile />
      )}

      {/* Phase 293 — Payment Verified tile (auto-hides when no Razorpay creds saved) */}
      {user?.role === 'academy_owner' && (
        <PaymentVerifiedTile />
      )}

      {/* Phase 282d — Quarterly Star Academy badge (silently hides if no win) */}
      {user?.role === 'academy_owner' && (
        <StarAcademyTile />
      )}

      {/* Phase 220 — Onboarding walkthrough video hub (silent if SA hasn't pinned any for this role) */}
      <SelfServiceWalkthroughSection placement="dashboard" />

      {/* Phase 262 — Academy Challenge leaderboard.  Self-hides when no
          active challenge exists, so this is safe to mount unconditionally. */}
      <AcademyChallengeTile user={user} />

      {/* Phase 266d — Top Moments embed performance.  Self-hides when no
          impressions in the last 30 days, so this is safe to mount
          unconditionally for academies that haven't embedded yet. */}
      <EmbedPerformanceTile />

      {/* Charts & Activity */}
      <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
        {/* Revenue Chart */}
        <Card className="lg:col-span-2" data-testid="revenue-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Revenue vs Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => '₹' + (v/1000) + 'K'} />
                  <Tooltip formatter={(value) => ['₹' + value.toLocaleString()]} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#0f766e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">
                No revenue data yet. Start by adding students and generating fees.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card data-testid="recent-activity-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <div className="space-y-3">
                {activity.slice(0, 6).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.type === 'fee_payment' ? <IndianRupee className="h-3.5 w-3.5 text-primary" /> : <UserPlus className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm truncate">{item.message}</p>
                      <p className="text-xs text-muted-foreground">{new Date(item.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
                No recent activity yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Phase 102 — Upcoming Birthdays widget */}
      {birthdays.length > 0 && (
        <Card data-testid="upcoming-birthdays-card" className="border-rose-200 bg-gradient-to-br from-rose-50/50 to-pink-50/50 dark:from-rose-950/10 dark:to-pink-950/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Cake className="h-4 w-4 text-rose-600" />
                Upcoming Birthdays
                <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-700 border-rose-300">{birthdays.length}</Badge>
              </CardTitle>
              <span className="text-xs text-muted-foreground">Next 14 days</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {birthdays.slice(0, 6).map((b) => {
                const initials = (b.name || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                const ribbon = b.days_until === 0 ? 'Today!' : b.days_until === 1 ? 'Tomorrow' : `in ${b.days_until} days`;
                return (
                  <div key={b.id} className="rounded-lg border border-rose-200 bg-background p-3 flex items-center gap-3" data-testid={`birthday-row-${b.id}`}>
                    <div className="h-10 w-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-xs font-semibold shrink-0">{initials}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{b.name}</p>
                      <p className="text-xs text-muted-foreground truncate">Turning {b.age_turning} · {ribbon}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        disabled={!b.guardian_phone || sendingWish === `${b.id}-whatsapp`}
                        onClick={() => handleSendWish(b, 'whatsapp')}
                        title={b.guardian_phone ? `Send WhatsApp wish to ${b.guardian_phone}` : 'No guardian phone on file'}
                        data-testid={`birthday-wish-whatsapp-${b.id}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0 text-blue-700 border-blue-300 hover:bg-blue-50"
                        disabled={!b.guardian_email || sendingWish === `${b.id}-email`}
                        onClick={() => handleSendWish(b, 'email')}
                        title={b.guardian_email ? `Send email wish to ${b.guardian_email}` : 'No guardian email on file'}
                        data-testid={`birthday-wish-email-${b.id}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            {birthdays.length > 6 && (
              <p className="text-xs text-muted-foreground mt-3 text-center">+ {birthdays.length - 6} more in the next 14 days</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
